import axios from "axios";
import { NotImplementedError } from "../../../errors";
import { PromiseHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter/utils";
import { BrandAnalysisFailed, BrandKitFetchFailed } from "./errors";
/**
 * Client-side bound on the analysis POST.
 *
 * Creative's own Cloud Run timeout is an hour, and the Apigee target in front
 * of it is set to 300s (`io.timeout.millis` in
 * `services/creative/apigee/apiproxy/targets/default.template.xml`), so that's
 * the real ceiling to stay under. 290s stays just under it.
 */
export const BRAND_ANALYSIS_TIMEOUT_MS = 290000;
const JSON_HEADERS = { "Content-Type": "application/json" };
/** The service returns the trace id as a response header, not in the body. */
const REQUEST_ID_HEADER = "x-request-id";
/**
 * Brand kits on the creative service.
 *
 * Backed by `${sdk.urls.creativeBaseUrl}/api/v1/creative`, reached through the
 * edge gateway's `/creative` route. Types come directly from
 * `@madhive/mad-types/openapi/creative/creativeSchemas` — no local model or
 * transformer, since mad-types already models the wire shape.
 *
 * Two operations:
 * - {@link analyze} — `POST /brand/analyze`, which researches a website and
 *   saves the resulting brand kit.
 * - {@link find} — `GET /brand-kits/{id}`, by id only.
 *
 * `analyze` returns the research payload as an untyped `data` object: the
 * proto models it as `google.protobuf.Struct`, so the spec can only describe
 * it as a bare object. {@link find} returns the same research as a typed
 * `BrandKitResponse` — colours, fonts and logos as real fields — and is the
 * better read for anything rendering them.
 */
class BrandKits extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "brandKits", { atomize: true });
    }
    /**
     * Researches a brand from its website and saves the resulting brand kit.
     *
     * Results are cached server-side on the normalized domain, so a repeat
     * request for the same site returns in well under a second with a `job_id`
     * of `cached-<domain>` rather than `rod-<unix>`. Callers should not assume
     * a slow call.
     *
     * @param request the website to research, and optionally the brand name and
     *   a resolved Google Place — passing the place is what populates the
     *   advertiser profile, audience and geo blocks on the response
     * @param signal lets a caller abort the request, e.g. a Cancel button
     * @return the analysis result and the trace id
     */
    async analyze(request, signal) {
        if (!request.website?.trim()) {
            throw new BrandAnalysisFailed(undefined, "Cannot research a brand without a website.");
        }
        try {
            const { data, headers } = await axios.post(`${this.collectionUrl}/brand/analyze`, request, { headers: JSON_HEADERS, timeout: BRAND_ANALYSIS_TIMEOUT_MS, signal });
            const requestId = headers?.[REQUEST_ID_HEADER];
            return requestId ? { ...data, request_id: requestId } : data;
        }
        catch (error) {
            // A cancelled request isn't a research failure -- let a caller tell it
            // apart via axios.isCancel() instead of getting a generic
            // BrandAnalysisFailed("Failed to research this brand...").
            if (axios.isCancel(error)) {
                throw error;
            }
            // A failed run is exactly when the trace id is wanted, so it has to come
            // off the error's response rather than the success path above.
            throw new BrandAnalysisFailed(error);
        }
    }
    async findItems(filters = {}) {
        const [id] = parseIdFilter(filters);
        if (!id) {
            throw new NotImplementedError("find without an id — brand kits are fetched one at a time");
        }
        const url = this.itemUrl(String(id));
        return this.cache
            .promise(url, () => axios
            .get(url, { headers: JSON_HEADERS })
            .then(({ data }) => (data ? [data] : [])))
            .catch((error) => {
            throw new BrandKitFetchFailed(error);
        });
    }
    /**
     * @deprecated
     * `save` is not supported here — the update endpoint takes its own request
     * shape rather than a partial brand kit, so it does not fit this handler.
     */
    saveItem() {
        throw new NotImplementedError("save");
    }
    deleteItem() {
        throw new NotImplementedError("delete");
    }
    /**
     * @deprecated
     * `make` is not supported — a brand kit only exists once the service has
     * researched it; see {@link analyze}.
     */
    make() {
        throw new NotImplementedError("make");
    }
    get collectionUrl() {
        // Paths match the mad-types-generated fetchers exactly — see
        // `mad-web/packages/core/mad-types/src/openapi/creative/creativeComponents.ts`
        // (`/api/v1/creative/brand/analyze`, `/api/v1/creative/brand-kits/{id}`).
        return `${this.sdk.urls.creativeBaseUrl}/api/v1/creative`;
    }
    itemUrl(id) {
        return `${this.collectionUrl}/brand-kits/${encodeURIComponent(id)}`;
    }
}
export default BrandKits;
//# sourceMappingURL=index.js.map